
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react';

import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import * as icons from 'react-bootstrap-icons';

import styles from './Buttons.module.css';

export const PrimaryDropdownButton = ({
    block = false,
    disabled = false,
    icon = null,
    iconMargin = 0,
    iconSize = 1,
    inverse = false,
    marginLeft = false,
    marginRight = false,
    marginTop = true,
    options = [],
    size = undefined,
    text = '',
}) => {

    const [open, setOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState(null);

    const dropdownRef = useRef(null);
    const dropdownMenuRef = useRef(null);

    const Icon = icon ? icons[icon] : null;

    // Calcular posición del dropdown
    const updateDropdownPosition = useCallback(() => {

        if (!dropdownRef.current) return;

        const button = dropdownRef.current.querySelector('button');

        if (!button) return;

        const rect = button.getBoundingClientRect();

        const menuHeight =
            dropdownMenuRef.current?.offsetHeight || 0;

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Abrir hacia arriba si no hay espacio abajo
        const openUpwards =
            menuHeight > 0 &&
            spaceBelow < menuHeight + 4 &&
            spaceAbove > spaceBelow;

        const availableHeight = Math.max(
            0,
            (openUpwards ? spaceAbove : spaceBelow) - 8
        );

        setDropdownPosition({
            top: openUpwards
                ? rect.top - 4
                : rect.bottom + 4,
            left: Math.max(
                4,
                Math.min(
                    rect.left,
                    window.innerWidth - rect.width - 4
                )
            ),
            width: Math.min(
                rect.width,
                window.innerWidth - 8
            ),
            transform: openUpwards
                ? 'translateY(-100%)'
                : 'none',
            maxHeight: availableHeight,
        });

    }, []);

    // Actualizar posición al abrir, hacer scroll o resize
    useLayoutEffect(() => {

        if (!open) return;

        updateDropdownPosition();

        window.addEventListener(
            'resize',
            updateDropdownPosition
        );

        document.addEventListener(
            'scroll',
            updateDropdownPosition,
            true
        );

        return () => {
            window.removeEventListener(
                'resize',
                updateDropdownPosition
            );

            document.removeEventListener(
                'scroll',
                updateDropdownPosition,
                true
            );
        };

    }, [open, updateDropdownPosition]);

    // Recalcular posición cuando el menú ya está renderizado
    useLayoutEffect(() => {

        if (!open || !dropdownMenuRef.current) return;

        updateDropdownPosition();

    }, [open, options.length, updateDropdownPosition]);

    // Cerrar al hacer clic fuera o presionar Escape
    useEffect(() => {

        if (!open) return;

        const handleClickOutside = (event) => {

            const clickedButton =
                dropdownRef.current?.contains(event.target);

            const clickedMenu =
                dropdownMenuRef.current?.contains(event.target);

            if (!clickedButton && !clickedMenu) {
                setOpen(false);
            }
        };

        const handleEscape = (event) => {

            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener(
            'mousedown',
            handleClickOutside
        );

        document.addEventListener(
            'keydown',
            handleEscape
        );

        return () => {
            document.removeEventListener(
                'mousedown',
                handleClickOutside
            );

            document.removeEventListener(
                'keydown',
                handleEscape
            );
        };

    }, [open]);

    // Cerrar cuando se deshabilita el botón
    useEffect(() => {

        if (disabled) {
            setOpen(false);
        }

    }, [disabled]);

    // Ejecutar acción seleccionada
    const handleOptionClick = (option) => {

        if (option.disabled) return;

        setOpen(false);

        option.onClick?.();
    };

    return (
        <>
            <div
                ref={dropdownRef}
                className={`dropdown ${
                    block ? styles.customButtonBlock : ''
                }`}
            >
                <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={open}
                    className={[
                        'btn',
                        styles.customButton,
                        inverse
                            ? styles.customPrimaryButtonInverse
                            : styles.customPrimaryButton,
                        marginTop ? styles.customButtonMT : '',
                        marginLeft ? styles.customButtonML : '',
                        marginRight ? styles.customButtonMR : '',
                        block ? styles.customButtonBlock : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => setOpen(prev => !prev)}
                    disabled={disabled}
                >
                    {Icon && (
                        <Icon
                            size={size}
                            className={styles.icon}
                            style={{
                                marginRight: `${iconMargin}px`,
                                transform: `scale(${iconSize})`,
                                transformOrigin: 'center',
                            }}
                        />
                    )}

                    {text}

                    <icons.ChevronDown
                        size={12}
                        style={{
                            marginLeft: '10px',
                            transform: open
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    />
                </button>
            </div>

            {open && dropdownPosition && createPortal(
                <div
                    ref={dropdownMenuRef}
                    className={`dropdown-menu show ${styles.customDropdownMenu}`}
                    style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        transform: dropdownPosition.transform,
                        maxHeight: dropdownPosition.maxHeight,
                        overflowY: 'auto',
                        zIndex: 1100,
                        margin: 0,
                    }}
                >
                    {options.map((option, index) => {

                        const OptionIcon = option.icon
                            ? icons[option.icon]
                            : null;

                        return (
                            <button
                                key={option.id ?? index}
                                type="button"
                                className="dropdown-item"
                                disabled={option.disabled}
                                onClick={() => handleOptionClick(option)}
                            >
                                {OptionIcon && (
                                    <OptionIcon
                                        size={16}
                                        style={{
                                            marginRight: '8px',
                                        }}
                                    />
                                )}

                                {option.text}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </>
    );
};

PrimaryDropdownButton.propTypes = {
    block: PropTypes.bool,
    disabled: PropTypes.bool,
    icon: PropTypes.string,
    iconMargin: PropTypes.number,
    iconSize: PropTypes.number,
    inverse: PropTypes.bool,
    marginLeft: PropTypes.bool,
    marginRight: PropTypes.bool,
    marginTop: PropTypes.bool,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            text: PropTypes.string.isRequired,
            icon: PropTypes.string,
            disabled: PropTypes.bool,
            onClick: PropTypes.func.isRequired,
        })
    ),
    size: PropTypes.number,
    text: PropTypes.string,
};
